import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  Download,
  Eye,
  Plus,
  Filter,
  Grid3X3,
  List,
  FileText,
  X,
  Lightbulb,
  ArrowLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import clsx from 'clsx';

// Template icon configuration type
interface TemplateIconConfig {
  icon: string;
  label: string;
  primaryColor: string;
  secondaryColor: string;
}

// Context and services
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { getTemplatesData, downloadTemplate, type Template, type Category, type TemplatesData } from '../../../services/templateService';
import { useTemplateAsDocument } from '../../../services/templateToDocumentService';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import TemplateSuggestionDialog from './TemplateSuggestionDialog';
import DocumentPreview from '../document-detail/DocumentPreview';
// DOCX thumbnail helpers
import { renderAsync } from 'docx-preview';
import html2canvas from 'html2canvas';
import { createRgbProcessedClone, oklchToRgb, checkForRemainingOklchColors } from '@/app/pages/essential-eight/components/color-utils';
import { parseOklchToRgb, sanitizeColors } from '@/app/pages/essential-eight/components/pdf-utils';
import { getThumb as idbGetThumb, setThumb as idbSetThumb } from '@/utils/thumbCache';
// Storage preview helper (align with DocumentPreview usage)
import { previewDocument } from '@/js/external-js/variableModules/downloadFromSDK.module.js';
import { firebaseConfig, storageRef } from '@/js/external-js/config/main_config';
import { notify } from '@/services/notificationService';
import { getEvidenceUrl } from '../assurancelibrary/downloadFromSDK.module';
import { useNavigate } from 'react-router-dom';
const getThumb = async (template: Template) => {
  const key = `template-${template.id}-thumb`;
  const url = await idbGetThumb(key);
  return url;
};

// Types are now imported from the service

const VIEWS = ['grid', 'list'] as const;
type ViewType = typeof VIEWS[number];

/**
 * Template icon configurations
 */
const TEMPLATE_ICONS: Record<string, TemplateIconConfig> = {
  checklists: {
    icon: 'fa-duotone fa-solid fa-clipboard-list-check',
    label: 'Checklists',
    primaryColor: '#53b43f',
    secondaryColor: '#abe1ac'
  },
  controls: {
    icon: 'fa-duotone fa-solid fa-grid-dividers',
    label: 'Controls',
    primaryColor: '#57bcdb',
    secondaryColor: '#ade4ef'
  },
  form: {
    icon: 'fa-duotone fa-solid fa-file-contract',
    label: 'Form',
    primaryColor: '#7f1aa9',
    secondaryColor: '#c99dda'
  },
  matrix: {
    icon: 'fa-duotone fa-solid fa-line-columns',
    label: 'Matrix',
    primaryColor: '#f8d450',
    secondaryColor: '#fbedb0'
  },
  objectives: {
    icon: 'fa-duotone fa-solid fa-bullseye-arrow',
    label: 'Objectives',
    primaryColor: '#e06d37',
    secondaryColor: '#f1c3ab'
  },
  obligations: {
    icon: 'fa-duotone fa-solid fa-lock-keyhole',
    label: 'Obligations',
    primaryColor: '#831b12',
    secondaryColor: '#c99f9b'
  },
  policy: {
    icon: 'fa-duotone fa-solid fa-ballot',
    label: 'Policy',
    primaryColor: '#4297e9',
    secondaryColor: '#a6d4f5'
  },
  process: {
    icon: 'fa-duotone fa-solid fa-gear',
    label: 'Process',
    primaryColor: '#1c48a6',
    secondaryColor: '#9fb6da'
  },
  register: {
    icon: 'fa-duotone fa-solid fa-folder',
    label: 'Register',
    primaryColor: '#e89a3e',
    secondaryColor: '#f4d6ab'
  },
  risks: {
    icon: 'fa-duotone fa-solid fa-triangle-exclamation',
    label: 'Risks',
    primaryColor: '#c62c22',
    secondaryColor: '#e3a0a0'
  },
  tasks: {
    icon: 'fa-duotone fa-solid fa-circle-check',
    label: 'Tasks',
    primaryColor: '#337345',
    secondaryColor: '#a4c6b3'
  }
};

/**
 * TemplateIcon component renders a Font Awesome icon with label
 */
const TemplateIcon: React.FC<{
  config: TemplateIconConfig;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  centered?: boolean;
}> = ({ config, size = 'md', showLabel = true, centered = true }) => {
  // Determine icon size based on the size prop
  const iconSize = {
    sm: 'text-2xl',
    md: 'text-4xl',
    lg: 'text-7xl'
  }[size];
  
  const labelSize = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg font-medium'
  }[size];
  
  return (
    <div className={`flex flex-col ${centered ? 'items-center justify-center' : ''} gap-2`}>
      <i 
        className={config.icon + ` ${iconSize}`} 
        style={{
          '--fa-primary-color': config.primaryColor,
          '--fa-secondary-color': config.secondaryColor,
          '--fa-secondary-opacity': '1'
        } as React.CSSProperties}
      />
      {showLabel && (
        <span 
          className={`${labelSize} font-semibold`} 
          style={{ color: config.primaryColor }}
        >
          {config.label}
        </span>
      )}
    </div>
  );
};

/**
 * Get template icon configuration based on template category or name
 */
const getTemplateIconConfig = (template: Template, categories: Category[]): TemplateIconConfig => {
  // Try to find the category first
  const category = categories.find(c => c.id === template.categoryId);
  const categoryName = category?.name?.toLowerCase() || '';
  const templateName = template.name.toLowerCase();
  
  // Match based on category name or template name keywords
  if (categoryName.includes('process') || templateName.includes('process') || categoryName.includes('procedure') || templateName.includes('procedure')) {
    return TEMPLATE_ICONS.process;
  } else if (categoryName.includes('policy') || templateName.includes('policy')) {
    return TEMPLATE_ICONS.policy;
  } else if (categoryName.includes('matrix') || templateName.includes('matrix') || categoryName.includes('register') || templateName.includes('register') || categoryName.includes('standard') || templateName.includes('standard')) {
    return TEMPLATE_ICONS.matrix;
  } else if (categoryName.includes('plan') || templateName.includes('plan') || categoryName.includes('template') || templateName.includes('template')) {
    return TEMPLATE_ICONS.form;
  } else if (categoryName.includes('procedure') || templateName.includes('procedure')) {
    return TEMPLATE_ICONS.process;
  } else if (categoryName.includes('risk') || templateName.includes('risk')) {
    return TEMPLATE_ICONS.risks;
  } else if (categoryName.includes('assurance') || templateName.includes('assurance') || categoryName.includes('task') || templateName.includes('task')) {
    return TEMPLATE_ICONS.tasks;
  } else if (categoryName.includes('support') || templateName.includes('support')) {
    return TEMPLATE_ICONS.controls;
  }
  
  // Default to policy icon if no match found
  return TEMPLATE_ICONS.policy;
};

const TemplateLibrary: React.FC = () => {
  const navigate = useNavigate();
  // URL params
  const [searchParams] = useSearchParams();

  // State
  const [view, setView] = useState<ViewType>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatesData, setTemplatesData] = useState<TemplatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [helpContent, setHelpContent] = useState(null);
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [processedTemplates, setProcessedTemplates] = useState<Template[]>([]);

  // Context
  const { user, isAuthReady, token } = useAuth();
  const { activeTenancy } = useTenancy();

  // Update selected category when URL params change
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Load help content
  useEffect(() => {
    getHelpContentByPage('template.library').then(setHelpContent);
  }, []);

  // Load templates data
  useEffect(() => {
    if (!isAuthReady || !user || !activeTenancy?.id) {
      if (!isAuthReady) {
        console.log('[TemplateLibrary] Waiting for auth to be ready...');
        return;
      }
      if (!user) {
        setError("Please log in to access templates.");
        setLoading(false);
        return;
      }
      if (!activeTenancy?.id) {
        setError("No company selected. Please select a tenancy.");
        setLoading(false);
        return;
      }
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('[TemplateLibrary] Loading templates for tenancy:', activeTenancy.id);
        const data = await getTemplatesData(activeTenancy.id, token || undefined);
        setTemplatesData(data);
      } catch (err) {
        console.error('[TemplateLibrary] Error loading templates:', err);
        setError('Failed to load templates. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthReady, user, token, activeTenancy?.id]);

  // Filter templates based on search and category
  const filteredTemplates = useMemo(() => {
    if (!templatesData) return [];

    let filtered = templatesData.templates;

    // Filter by category
    if (selectedCategory !== 'all') {
      // Simple category matching by name or tag
      filtered = filtered.filter(template => {
        // Check if the template's category matches the selected category
        const templateCategory = template.categoryId?.toLowerCase() || '';
        const categoryName = selectedCategory.toLowerCase();
        
        // Match by category ID
        if (templateCategory === categoryName) {
          return true;
        }
        
        // Match by tags
        if (template.metadata?.tags && Array.isArray(template.metadata.tags)) {
          return template.metadata.tags.some(tag => 
            tag.toLowerCase() === categoryName || 
            tag.toLowerCase().includes(categoryName)
          );
        }
        
        // Match by description or name as fallback
        return (
          template.name.toLowerCase().includes(categoryName) ||
          template.description?.toLowerCase().includes(categoryName)
        );
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query) ||
        template.metadata.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [templatesData, selectedCategory, searchQuery]);

  // Filter categories based on available templates and rename "Supporting Documents" to "Forms"
  const categoryTree = useMemo(() => {
    if (!templatesData) return [];

    // Get all category IDs that have templates
    const categoriesWithTemplates = new Set(
      templatesData.templates.map(template => template.categoryId)
    );

    // All possible categories
    const allCategories = [
      { id: 'all', name: 'Show All', icon: 'grid-3x3', groupName: 'All' },
      { id: 'process-documents', name: 'Process Documents', icon: 'file-alt', groupName: 'Process Documents' },
      { id: 'policies', name: 'Policies', icon: 'folder', groupName: 'Policies' },
      { id: 'standards-matrices', name: 'Standards and Matrices', icon: 'table', groupName: 'Standards and Matrices' },
      { id: 'plans-templates', name: 'Plans and Templates', icon: 'file-word', groupName: 'Plans and Templates' },
      { id: 'procedures', name: 'Procedures', icon: 'book', groupName: 'Procedures' },
      { id: 'risk-register', name: 'Risk Register', icon: 'book', groupName: 'Risk Register' },
      { id: 'assurance-tasks', name: 'Assurance Tasks', icon: 'book', groupName: 'Assurance Tasks' },
      { id: 'supporting-docs', name: 'Supporting Forms', icon: 'book', groupName: 'Forms' }
    ];

    // Filter categories: always include "all", but only include others if they have templates
    return allCategories.filter(category => 
      category.id === 'all' || categoriesWithTemplates.has(category.id)
    );
  }, [templatesData]);

  // Handlers
  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  const handlePreview = async (template: Template) => {
    if (!template.downloadUrl) {
      toast.error('Preview not available for this template');
      return;
    }

    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const handleDownload = async (template: Template) => {
    try {
      toast.loading('Preparing download...');
      await downloadTemplate(template, activeTenancy!.id);
      toast.dismiss();
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download template');
      console.error('Download error:', error);
    }
  };

  const handleUseTemplate = async (template: Template) => {
    try {
      toast.loading(`Importing to document library`);

      // Use the template directly since names are already processed by getTemplatesData
      const createdDocument = await useTemplateAsDocument(template, activeTenancy!.id);

      toast.dismiss();
      toast.success(`Template "${template.name}" successfully added to your documents`);

      // Dispatch custom event for other components to handle (optional)
      const event = new CustomEvent('templateUsed', {
        detail: {
          template: template,
          document: createdDocument,
          documentId: createdDocument.docID
        }
      });

      document.dispatchEvent(event);

      // Add notification to tenancy
      notify.success(
        'A new document has been added to your document library',
        `The document "${template.name}" has been added to your document library`,
        true,
        user?.uid,
        "/documentlibrary"
      );

      // Redirect to document detail
      navigate(`/documentdetail/${createdDocument.docID}`);
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to use template: ${error.message}`);
      console.error('[TemplateLibrary] Error using template:', error);
    }
  };

  const handleSuggestTemplate = () => {
    setSuggestionDialogOpen(true);
  };

  if (loading) {
    return <TemplateLibrarySkeleton />;
  }

  if (error) {
    return (
      <div className="w-full min-h-[80vh] bg-white dark:bg-zinc-900 rounded-xl shadow-sm mx-auto mt-8 px-8 py-6 flex flex-col items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Error Loading Templates</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[80vh] bg-white dark:bg-zinc-900 rounded-xl shadow-sm mx-auto mt-8 px-8 py-6 flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b pb-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="title text-xl font-semibold flex items-center">
              Templates Gallery
              <span className="ml-2">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle text-muted-foreground">
              Browse and use document templates for your organization
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Back to Docs Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="mr-2"
              onClick={() => navigate('/documentlibrary')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Docs
            </Button>

            {/* View Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  {view === 'list' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setView('list')}>
                  <List className="w-4 h-4 mr-2" />
                  List View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setView('grid')}>
                  <Grid3X3 className="w-4 h-4 mr-2" />
                  Grid View
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-6">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 relative">
          <div className="space-y-2"> {/* No bottom padding needed since button is fixed to viewport */}
            {categoryTree.map((category) => (
              <CategoryItem
                key={category.id}
                category={category}
                onClick={(categoryId) => setSelectedCategory(categoryId)}
                selectedCategoryId={selectedCategory}
              />
            ))}
          </div>
          
          {/* Suggest Template button - fixed to viewport bottom */}
          <div className="fixed bottom-10 w-64 pr-4 z-10">
            <div className="bg-gradient-to-t from-white via-white dark:from-zinc-900 dark:via-zinc-900 pt-6 pb-0">
              <Button 
                onClick={handleSuggestTemplate} 
                variant="default" 
                size="sm"
                className="w-full shadow-md hover:shadow-lg transition-all font-medium"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Request Template
              </Button>
            </div>
          </div>
        </div>

        {/* Templates Grid/List */}
        <div className="flex-1">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Templates Found</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'Try adjusting your search terms' : 'No templates available in this category'}
              </p>
            </div>
          ) : (
            <div className={clsx(
              view === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-4'
            )}>
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  view={view}
                  categories={templatesData?.categories || []}
                  isSelected={selectedTemplate?.id === template.id}
                  onSelect={() => handleTemplateSelect(template)}
                  onPreview={() => handlePreview(template)}
                  onDownload={() => handleDownload(template)}
                  onUse={() => handleUseTemplate(template)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        template={selectedTemplate}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onDownload={() => selectedTemplate && handleDownload(selectedTemplate)}
        onUse={() => selectedTemplate && handleUseTemplate(selectedTemplate)}
      />

      {/* Template Suggestion Dialog */}
      <TemplateSuggestionDialog
        open={suggestionDialogOpen}
        onOpenChange={setSuggestionDialogOpen}
        categories={templatesData?.categories || []}
      />
    </div>
  );
};

// Category Item Component
const CategoryItem: React.FC<{
  category: Category;
  onClick: (categoryId: string) => void;
  selectedCategoryId?: string;
  isChild?: boolean;
}> = ({ category, onClick, selectedCategoryId, isChild = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Only highlight if this exact category is selected
  // For parent categories with children, don't highlight when selected (just expand)
  const isSelected = selectedCategoryId === category.id && (!category.items?.length || isChild);

  return (
    <div>
      <button
        onClick={() => {
          onClick(category.id);
          if (category.items?.length) {
            setIsExpanded(!isExpanded);
          }
        }}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors',
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
      >
        {category.id === 'all' ? (
          <Grid3X3 className="w-4 h-4" />
        ) : (
          <FileText className="w-4 h-4" />
        )}
        <span className="flex-1 text-sm">{category.name}</span>
        {category.items?.length && (
          <ChevronRight className={clsx(
            'w-4 h-4 transition-transform',
            isExpanded && 'rotate-90'
          )} />
        )}
      </button>

      {category.items && isExpanded && (
        <div className="ml-6 mt-1 space-y-1">
          {category.items.map((item) => (
            <CategoryItem
              key={item.id}
              category={item}
              onClick={onClick}
              selectedCategoryId={selectedCategoryId}
              isChild={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Template Card Component
const TemplateCard: React.FC<{
  template: Template;
  view: ViewType;
  isSelected: boolean;
  categories: Category[];
  onSelect: () => void;
  onPreview: () => void;
  onDownload: () => void;
  onUse: () => void;
}> = ({ template, view, isSelected, categories, onSelect, onPreview, onDownload, onUse }) => {
  // Generate a thumbnail for DOCX templates from downloadUrl
  const [docxThumb, setDocxThumb] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const renderContainerRef = useRef<HTMLDivElement | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  // Simple global concurrency limiter for heavy thumbnail generation
  const maxConcurrent = 2;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!(window as any).__tmplThumbSem) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (window as any).__tmplThumbSem = { running: 0, waiters: [] as Array<() => void> };
  }
  const sem = (window as any).__tmplThumbSem as { running: number; waiters: Array<() => void> };
  const acquire = async () => {
    if (sem.running >= maxConcurrent) {
      await new Promise<void>((resolve) => sem.waiters.push(resolve));
    }
    sem.running++;
  };
  const release = () => {
    sem.running = Math.max(0, sem.running - 1);
    const next = sem.waiters.shift();
    if (next) next();
  };

  // Observe visibility of the card to delay heavy work
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Once visible, we can stop observing to avoid repeated triggers
            io.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '200px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Fetch preview image from Firebase Storage
  const [storagePreviewUrl, setStoragePreviewUrl] = useState<string | null>(null);
  
  useEffect(() => {
    let cancelled = false;
    
    const fetchPreview = async () => {
      if (!template.previewUrl || !isVisible) {
        return;
      }
      
      try {
        // Preview images are stored at: templates/templateLibrary/previews/{template-id}.png
        // template.previewUrl contains just the filename (e.g., "my-template.png")
        const previewsFolder = `templates/templateLibrary/previews`;
        const fileName = template.previewUrl;
        const previewPath = `${previewsFolder}/${fileName}`;
        
        const url = await getEvidenceUrl({
          fileName,
          path: previewPath
        });
        
        if (!cancelled && url) {
          setStoragePreviewUrl(url);
        }
      } catch (error) {
        console.error('[TemplateCard] Failed to fetch preview from storage:', error);
        if (!cancelled) {
          setStoragePreviewUrl(null);
        }
      }
    };
    
    fetchPreview();
    
    return () => {
      cancelled = true;
    };
  }, [template.previewUrl, isVisible]);

  const previewSrc = useMemo(() => {
    // Priority: Storage preview > DOCX thumbnail > null
    return storagePreviewUrl || docxThumb || null;
  }, [storagePreviewUrl, docxThumb]);

  if (view === 'list') {
    return (
      <Card ref={cardRef} className={clsx(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected
      )} onClick={onSelect}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
              <TemplateIcon 
                config={getTemplateIconConfig(template, categories)} 
                size="sm"
                showLabel={false}
                centered={true}
              />
            </div>

            <div className="flex-1 min-w-0">
              <h4 className="font-medium truncate">{template.name}</h4>
              <p className="text-sm text-muted-foreground truncate">
                {template.description || 'No description available'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-xs">
                  v{template.metadata.version}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  by {template.metadata.author}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onPreview(); }}>
                <Eye className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDownload(); }}>
                <Download className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={(e) => { e.stopPropagation(); onUse(); }}>
                Use
              </Button>
            </div>
          </div>
        </CardContent>
        {/* Hidden offscreen container for rendering DOCX prior to rasterization */}
        <div
          ref={renderContainerRef}
          aria-hidden
          style={{ position: 'fixed', left: '-10000px', top: 0, width: '900px', background: 'white', padding: 0 }}
        />
      </Card>
    );
  }

  return (
    <Card ref={cardRef} className={clsx(
      'overflow-hidden group cursor-pointer hover:shadow-md transition-all',
      isSelected
    )} onClick={onSelect}>
      <CardContent className="p-4">
        {/* A4 aspect ratio: 1:1.414 (210mm x 297mm) */}
        <div className="w-full bg-muted rounded-md mb-3 overflow-hidden" style={{ aspectRatio: '1 / 1.414' }}>
          {previewSrc ? (
            <img 
              src={previewSrc} 
              alt={template.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <TemplateIcon 
                config={getTemplateIconConfig(template, categories)} 
                size="md"
                showLabel={false}
                centered={true}
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="font-medium truncate">{template.name}</h4>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>v{template.metadata.version}</span>
            <span>by {template.metadata.author}</span>
          </div>

          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onPreview(); }} className="flex-1">
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onUse(); }} className="flex-1">
              Use
            </Button>
          </div>
        </div>
      </CardContent>
      {/* Hidden offscreen container for rendering DOCX prior to rasterization */}
      <div
        ref={renderContainerRef}
        aria-hidden
        style={{ position: 'fixed', left: '-10000px', top: 0, width: '900px', background: 'white', padding: 0 }}
      />
    </Card>
  );
};

// Preview Modal Component
const TemplatePreviewModal: React.FC<{
  template: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: () => void;
  onUse: () => void;
}> = ({ template, open, onOpenChange, onDownload, onUse }) => {
  // Close modal when fullscreen is triggered to prevent scroll blocking
  useEffect(() => {
    if (!open) return;
    
    const handleFullscreen = () => {
      // Close the modal when fullscreen overlay is created
      onOpenChange(false);
    };
    
    // Listen for fullscreen preview creation (the overlay has class 'fullscreen-preview')
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.classList.contains('fullscreen-preview')) {
            handleFullscreen();
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true });
    
    return () => observer.disconnect();
  }, [open, onOpenChange]);

  if (!template) return null;

  // Decode the template name for display
  const displayName = decodeURIComponent(template.name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Template Preview: {displayName}</span>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {template.downloadUrl ? (
            <DocumentPreview fileName={template.downloadUrl} templateLibraryPreview />
          ) : (
            <div className="w-full h-[60vh] bg-muted rounded-md flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Preview not available</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
          <Button onClick={() => { onUse(); onOpenChange(false); }}>
            Use Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Skeleton Loader Component
const TemplateLibrarySkeleton: React.FC = () => {
  return (
    <div className="w-full min-h-[80vh] bg-white dark:bg-zinc-900 rounded-xl shadow-sm mx-auto mt-8 px-8 py-6 flex flex-col gap-6">
      {/* Header Skeleton */}
      <div className="flex flex-col gap-4 border-b pb-4">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-36 mr-2" /> {/* Back to Docs button */}
            <Skeleton className="h-9 w-10" /> {/* View toggle button */}
          </div>
        </div>
        <Skeleton className="h-10 w-full" />
      </div>

      {/* Content Skeleton */}
      <div className="flex-1 flex gap-6">
        {/* Sidebar Skeleton */}
        <div className="w-64 space-y-2 relative">
          <div> {/* No padding needed since button is fixed */}
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
          
          {/* Suggest Template button skeleton - fixed to viewport */}
          <div className="fixed bottom-10 w-64 pr-4 z-10">
            <div className="bg-gradient-to-t from-white via-white dark:from-zinc-900 dark:via-zinc-900 pt-6 pb-0">
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          </div>
        </div>

        {/* Grid Skeleton */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="aspect-video mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-3 w-24 mb-2" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;
